import React, { useEffect, useState, useCallback } from "react";
import { AppShell } from "@/components/AppShell";
import { KpiCard, Skeleton, EmptyState } from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { api, extractErr } from "@/lib/api";
import { toast } from "sonner";
import { PlanGate, featureAllowedByPlan } from "@/components/PlanGate";
import { useAuth } from "@/lib/auth";
import {
  Boxes, AlertTriangle, Clock, PackageCheck, ChevronRight, ChevronDown,
  Search, CalendarClock,
} from "lucide-react";

function fmtDate(v) {
  if (!v) return "—";
  try { return new Date(v).toLocaleDateString(); } catch { return String(v); }
}

export default function LotReport() {
  const { company } = useAuth();
  const allowed = featureAllowedByPlan(company?.plan || "free_trial", "lot_tracking");
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({});
  const [loading, setLoading] = useState(true);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [search, setSearch] = useState("");
  const [expandedId, setExpandedId] = useState(null);

  const load = useCallback(async () => {
    if (!allowed) { setLoading(false); return; }
    setLoading(true);
    try {
      const params = {};
      if (includeInactive) params.include_inactive = true;
      const { data } = await api.get("/lots/report", { params });
      setRows(data.rows || []);
      setTotals(data.totals || {});
    } catch (e) { toast.error(extractErr(e, "Failed to load lot report")); }
    setLoading(false);
  }, [allowed, includeInactive]);

  useEffect(() => { load(); }, [load]);

  if (!allowed) {
    return (
      <AppShell title="Deep Lot Reporting" subtitle="Batch / lot breakdown by SKU">
        <PlanGate feature="Lot & batch reporting" planRequired="Enterprise"><div className="h-32" /></PlanGate>
      </AppShell>
    );
  }

  const filtered = search
    ? rows.filter((r) => (r.item_name || "").toLowerCase().includes(search.toLowerCase()) || (r.item_sku || "").toLowerCase().includes(search.toLowerCase()))
    : rows;

  return (
    <AppShell
      title="Deep Lot Reporting"
      subtitle="Batch / lot breakdown by SKU with expiry tracking"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <KpiCard icon={Boxes} label="Items with lots" value={loading ? "…" : String(totals.items ?? 0)} hint="Distinct SKUs tracked" />
        <KpiCard icon={PackageCheck} label="Total lots" value={loading ? "…" : String(totals.total_lots ?? 0)} hint="Across all items" />
        <KpiCard icon={CalendarClock} label="Expiring ≤ 30 days" value={loading ? "…" : String(totals.expiring_30d ?? 0)} hint="Needs attention" />
        <KpiCard icon={AlertTriangle} label="Already expired" value={loading ? "…" : String(totals.expired ?? 0)} hint="Clear or consume" />
      </div>

      <div className="rounded-2xl border border-border bg-card">
        <div className="p-3 border-b border-border flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[220px] max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input placeholder="Search by product name or SKU…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="lot-report-search" />
          </div>
          <div className="flex items-center gap-2">
            <Switch id="incl-inactive" checked={includeInactive} onCheckedChange={setIncludeInactive} data-testid="lot-report-inactive-toggle" />
            <Label htmlFor="incl-inactive" className="text-xs cursor-pointer">Include deactivated lots</Label>
          </div>
        </div>

        {loading ? (
          <div className="p-4 space-y-3">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-16" />)}</div>
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={Boxes}
            title="No lots tracked yet"
            description="Lots are created automatically when you receive goods with a lot number (goods intake or purchase orders)."
            className="border-0"
          />
        ) : (
          <div className="divide-y divide-border">
            {filtered.map((r) => {
              const expanded = expandedId === r.item_id;
              return (
                <div key={r.item_id} data-testid={`lot-report-row-${r.item_id}`}>
                  <button onClick={() => setExpandedId(expanded ? null : r.item_id)}
                    className="w-full text-left p-3 sm:p-4 flex items-center gap-3 hover:bg-accent/30 transition-colors">
                    {expanded ? <ChevronDown className="w-4 h-4 shrink-0" /> : <ChevronRight className="w-4 h-4 shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{r.item_name}</div>
                      <div className="text-xs text-muted-foreground font-mono">{r.item_sku}</div>
                    </div>
                    <div className="hidden md:flex items-center gap-4 text-xs">
                      <div className="text-right">
                        <div className="text-muted-foreground">Total</div>
                        <div className="font-mono tabular-nums">{r.total_qty} {r.unit}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-muted-foreground">Lots</div>
                        <div className="font-mono tabular-nums">{r.lot_count}</div>
                      </div>
                      {r.expiring_30d > 0 && (
                        <div className="rounded-full bg-amber-500/10 text-amber-600 dark:text-amber-400 px-2 py-0.5 text-[11px]">
                          {r.expiring_30d} expiring
                        </div>
                      )}
                      {r.expired_count > 0 && (
                        <div className="rounded-full bg-destructive/10 text-destructive px-2 py-0.5 text-[11px]">
                          {r.expired_count} expired
                        </div>
                      )}
                      {typeof r.next_expiry === "number" && (
                        <div className="text-right">
                          <div className="text-muted-foreground">Next expiry</div>
                          <div className={`font-mono tabular-nums ${r.next_expiry < 0 ? "text-destructive" : r.next_expiry <= 30 ? "text-amber-600 dark:text-amber-400" : ""}`}>
                            {r.next_expiry}d
                          </div>
                        </div>
                      )}
                    </div>
                  </button>

                  {expanded && (
                    <div className="px-4 pb-4">
                      <div className="rounded-lg border border-border overflow-hidden">
                        <table className="w-full text-sm" data-testid={`lot-detail-table-${r.item_id}`}>
                          <thead className="bg-muted/50 text-xs uppercase tracking-wide text-muted-foreground">
                            <tr>
                              <th className="text-left font-medium p-2">Lot #</th>
                              <th className="text-right font-medium p-2">Qty</th>
                              <th className="text-left font-medium p-2">Received</th>
                              <th className="text-left font-medium p-2">Expires</th>
                              <th className="text-left font-medium p-2">Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {r.lots.map((l) => (
                              <tr key={l.id} className="border-t border-border">
                                <td className="p-2 font-mono text-xs">{l.lot_number || "—"}</td>
                                <td className="p-2 text-right font-mono tabular-nums">{l.quantity} {r.unit}</td>
                                <td className="p-2 text-xs">{fmtDate(l.received_date)}</td>
                                <td className="p-2 text-xs">
                                  {l.expiry_date ? fmtDate(l.expiry_date) : <span className="text-muted-foreground">No expiry</span>}
                                </td>
                                <td className="p-2">
                                  {!l.active ? (
                                    <span className="inline-flex rounded-full bg-muted text-muted-foreground px-2 py-0.5 text-[11px]">Inactive</span>
                                  ) : typeof l.days_until_expiry === "number" && l.days_until_expiry < 0 ? (
                                    <span className="inline-flex items-center gap-1 rounded-full bg-destructive/10 text-destructive px-2 py-0.5 text-[11px]">
                                      <AlertTriangle className="w-3 h-3" />Expired
                                    </span>
                                  ) : typeof l.days_until_expiry === "number" && l.days_until_expiry <= 30 ? (
                                    <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/10 text-amber-600 dark:text-amber-400 px-2 py-0.5 text-[11px]">
                                      <Clock className="w-3 h-3" />{l.days_until_expiry}d
                                    </span>
                                  ) : (
                                    <span className="inline-flex rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 px-2 py-0.5 text-[11px]">Active</span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </AppShell>
  );
}
